import classNames from "classnames";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import { addCatalogItem } from "../../../app/catalog/catalog.slice";
import { fetchDecks } from "../../../app/decks/decks.slice";
import { toast } from "sonner";
import { createTexts, createTextsMedia, getTextID, updateTexts } from "../../../api/texts.service";
import Icon from "../../../components/Icon";
import styles from "./Add.module.sass";
import Decks from "./components/Decks";
import Image from "./components/Image";
import NameAndInfo from "./components/NameAndInfo";
import Preview from "./components/Preview";
import Price from "./components/Price";
import SuitableForHsk from "./components/SuitableForHsk";
import TopWords from "./components/TopWords";
import Video from "./components/Video";

function getCategoryTitles(categories, categoryId, subcategoryId) {
  if (!categories?.length || !categoryId || !subcategoryId) return { categoryTitle: '', subcategoryTitle: '' };
  const cat = categories.find((c) => String(c._id) === String(categoryId));
  const sub = cat?.subCategories?.find((s) => String(s._id) === String(subcategoryId));
  const getTitle = (obj) => obj?.translations?.find((t) => t?.lang === 'en')?.trans || obj?.title || '';
  return {
    categoryTitle: getTitle(cat) || String(categoryId),
    subcategoryTitle: getTitle(sub) || String(subcategoryId),
  };
}

const isMongoId = (value) => typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value);

const toMongoId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return isMongoId(value) ? value : null;
  if (typeof value === "object") {
    if (isMongoId(value?._id)) return value._id;
    if (isMongoId(value?.value)) return value.value;
    if (isMongoId(value?.id)) return value.id;
  }
  return null;
};

const AddText = ({ isEdit, embedded = false, onRegisterSave }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const { category } = useSelector((state) => state);

  useEffect(() => {
    dispatch(fetchDecks());
  }, [dispatch]);

  // Preselect category/subcategory when navigating from Catalog (Add text from subcategory)
  useEffect(() => {
    const state = location?.state;
    if (!id && state?.fromCatalog && state?.categoryId && state?.subcategoryId) {
      setCategorySelected(state.categoryId);
      setSubCategorySelected(state.subcategoryId);
      history.replace(location.pathname, {}); // Clear state to avoid re-applying on remount
    }
  }, [location?.state, location?.pathname, id, history]);

  const [objectTitle, setObjectTitle] = useState({
    ES: "",
    EN: "",
    FR: "",
    RU: "",
    PT: "",
  });
  const [objectTextarea, setObjectTextarea] = useState({
    ES: "",
    EN: "",
    FR: "",
    RU: "",
    PT: "",
  });
  const [textAvailability, setTextAvailability] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  const [categorySelected, setCategorySelected] = useState("");
  const [subCategorySelected, setSubCategorySelected] = useState("");

  const [levelSelected, setLevelSelected] = useState("");

  const [imageSelected, setImageSelected] = useState("");
  const [videoSelected, setVideoSelected] = useState("");

  const [priceAvailability, setPriceAvailability] = useState(false);
  const [textPrice, setTextPrice] = useState("");

  const [decksSelected, setDecksSelected] = useState([]);
  const [publish, setPublish] = useState(false);
  const [publishDate, setPublishDate] = useState(null);


  const [suitableForHskSelected, setSuitableForHskSelected] = useState([]);

  const [cardsSelected, setCardsSelected] = useState([]);

  const [text, setText] = useState("");

  const [loading, setLoading] = useState(false);
  const [isEditDataLoading, setIsEditDataLoading] = useState(false);
  const isMountedRef = useRef(true);
  const latestSaveHandlerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const handleGetTextID = async () => {
      setIsEditDataLoading(true);
      try {
        let response = await getTextID("", id);

        if (cancelled) return;

        if (response?.status === 200) {
          const data = response?.data;
          setCategorySelected(data?.category?._id);
          setSubCategorySelected(data?.subCategory?._id);
          setLevelSelected(data?.level);
          setObjectTitle({
            ES: data?.title?.find((c) => c?.lang === "es")?.trans,
            EN: data?.title?.find((c) => c?.lang === "en")?.trans,
            FR: data?.title?.find((c) => c?.lang === "fr")?.trans,
            RU: data?.title?.find((c) => c?.lang === "ru")?.trans,
            PT: data?.title?.find((c) => c?.lang === "pt")?.trans,
          });
          setObjectTextarea({
            ES: data?.description?.find((c) => c?.lang === "es")?.trans,
            EN: data?.description?.find((c) => c?.lang === "en")?.trans,
            FR: data?.description?.find((c) => c?.lang === "fr")?.trans,
            RU: data?.description?.find((c) => c?.lang === "ru")?.trans,
            PT: data?.description?.find((c) => c?.lang === "pt")?.trans,
          });
          setCardsSelected(data?.topWords ?? []);
          setDecksSelected(data?.decks ?? []);
          if (data?.suitableForHsk) {
            setSuitableForHskSelected([data?.suitableForHsk]);
          }
          setImageSelected(data?.imageUrl ?? "");
          setVideoSelected(data?.videoUrl ?? data?.audioUrl ?? "");
          setText(data?.text ?? "");
          setPriceAvailability(data?.premium?.active ?? false);
          setTextPrice(data?.premium?.cost ?? "");
          setTextAvailability(data?.active ?? false);
          setPublish(data?.isNew ?? false);
          setPublishDate(data?.publishedDate ?? null);
        }
      } finally {
        if (!cancelled) {
          setIsEditDataLoading(false);
        }
      }
    };

    if (id) {
      handleGetTextID();
    } else {
      setIsEditDataLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (categorySelected && category) {
      setSubCategoryOptions(
        category?.value
          ?.filter((val) => val._id === categorySelected)?.[0]
          ?.subCategories?.map((val) => {
            return {
              label: val.title,
              value: val._id,
            };
          })
      );
    }
  }, [categorySelected, category]);

  useEffect(() => {
    if (category?.value?.length > 0) {
      setCategoryOptions(
        category?.value?.map((val) => {
          return {
            label: val.title,
            value: val._id,
          };
        })
      );
    }
  }, [category]);

  const nameAndInfoProps = {
    objectTitle,
    setObjectTitle,
    objectTextarea,
    setObjectTextarea,
    textAvailability,
    setTextAvailability,
    categoryOptions,
    setCategoryOptions,
    subCategoryOptions,
    setSubCategoryOptions,
    categorySelected,
    setCategorySelected,
    subCategorySelected,
    setSubCategorySelected,
    levelSelected,
    setLevelSelected,
    setPublish,
    publish,
    publishDate
  };

  const imageProps = {
    imageSelected,
    setImageSelected,
    isEdit,
  };

  const videoProps = {
    videoSelected,
    setVideoSelected,
  };

  const decksProps = {
    decksSelected,
    setDecksSelected,
    isEdit,
  };

  const suitableForHskProps = {
    suitableForHskSelected,
    setSuitableForHskSelected,
    isEdit,
  };

  const topWordsProps = {
    cardsSelected,
    setCardsSelected,
    isEdit,
  };

  const priceProps = {
    priceAvailability,
    setPriceAvailability,
    textPrice,
    setTextPrice,
  };

  const textProps = {
    text,
    setText,
  };

  const handleSaveChanges = async ({ manual = false } = {}) => {
    // Prevent accidental autosaves on mount/navigation: only allow explicit manual save flow.
    if (!manual) return;
    // Prevent empty PUT payloads while edit data is still loading.
    if (isEdit && isEditDataLoading) return;
    setLoading(true);

    const resolvedCategoryId = toMongoId(subCategorySelected);
    const resolvedDeckIds = (decksSelected || []).map(toMongoId).filter(Boolean);
    const resolvedSuitableForHsk = toMongoId(suitableForHskSelected?.[0]) || null;
    const resolvedTopWordIds = (cardsSelected || []).map(toMongoId).filter(Boolean);

    let json = {
      active: textAvailability,
      title: Object.entries(objectTitle).map((c) => {
        return {
          active: c?.[1] ? true : false,
          lang: c?.[0]?.toLowerCase(),
          trans: c?.[1],
        };
      }),
      description: Object.entries(objectTextarea).map((c) => {
        return {
          active: c?.[1] ? true : false,
          lang: c?.[0]?.toLowerCase(),
          trans: c?.[1],
        };
      }),
      category: resolvedCategoryId || undefined,
      level: levelSelected,
      topWords: resolvedTopWordIds,
      decks: resolvedDeckIds,
      suitableForHsk: resolvedSuitableForHsk,
      text,
      premium: {
        active: priceAvailability,
        cost: textPrice || 0,
      },
      isNew: publish
    };

    let textId = null;
    try {
      if (isEdit) {
        const resolvedTextId = toMongoId(id);
        if (!resolvedTextId) {
          throw new Error("Invalid text id");
        }
        json._id = resolvedTextId;
        textId = resolvedTextId;
        let response = await updateTexts(json);
        if (!isMountedRef.current) return;
        if (response?.data?._id) {
          toast.success(t("toast.textUpdated"));
        } else {
          toast.error(t("toast.errorOccurred"));
        }
      } else {
        let response = await createTexts(json);
        if (!isMountedRef.current) return;
        if (response?.data?._id) {
          textId = response?.data?._id;
          const newText = response.data;
          if (categorySelected && subCategorySelected) {
            const { categoryTitle, subcategoryTitle } = getCategoryTitles(category?.value ?? [], categorySelected, subCategorySelected);
            dispatch(addCatalogItem({
              item: {
                _id: newText._id,
                title: objectTitle?.EN || objectTitle?.ES || '',
                titleTranslations: Object.entries(objectTitle || {}).map(([lang, trans]) => ({ lang: lang.toLowerCase(), trans })),
                level: levelSelected,
                premium: { active: priceAvailability, cost: textPrice },
              },
              categoryId: categorySelected,
              subcategoryId: subCategorySelected,
              type: 'text',
              categoryTitle,
              subcategoryTitle,
            }));
          }
          toast.success(t("toast.textCreated"));
          history.push(`/texts/content/${newText._id}`);
        } else {
          toast.error(t("toast.errorOccurred"));
        }
      }

      if (imageSelected && typeof imageSelected !== "string") {
        let bodyImage = new FormData();
        bodyImage.append("file", imageSelected);
        bodyImage.append("type", "image");
        bodyImage.append("_id", textId);
        await createTextsMedia(bodyImage);
      }

      if (videoSelected && typeof videoSelected !== "string") {
        let bodyMedia = new FormData();
        bodyMedia.append("file", videoSelected);
        bodyMedia.append("type", String(videoSelected?.type || "").startsWith("audio/") ? "audio" : "video");
        bodyMedia.append("_id", textId);
        await createTextsMedia(bodyMedia);
      }
      if (isMountedRef.current) setLoading(false);
    } catch (e) {
      if (isMountedRef.current) {
        setLoading(false);
        toast.error(t("toast.errorOccurred"));
      }
    }
  };

  const runManualSave = async () => {
    await handleSaveChanges({ manual: true });
  };

  useEffect(() => {
    latestSaveHandlerRef.current = runManualSave;
  });

  // In embedded mode, keep a stable wrapper so TextEditor always executes the latest save logic/state.
  useEffect(() => {
    if (!embedded || !onRegisterSave) return;
    const disabled = loading || (isEdit && isEditDataLoading);
    const wrappedSave = async () => {
      if (typeof latestSaveHandlerRef.current === "function") {
        await latestSaveHandlerRef.current();
      }
    };
    onRegisterSave({ save: wrappedSave, disabled });
    return () => onRegisterSave(null);
  }, [embedded, onRegisterSave, loading, isEdit, isEditDataLoading]);

  return (
    <div className={styles.spacey}>
      <div style={{ padding: "0px 40px 40px 40px" }} className={styles.row}>
        <div className={styles.col}>
          <NameAndInfo {...nameAndInfoProps} />
          <Image {...imageProps} />
          <Video {...videoProps} />
          <TopWords {...topWordsProps} />
          <Decks {...decksProps} />
          <SuitableForHsk {...suitableForHskProps} />
          <Price {...priceProps} />
        </div>
        <div className={styles.col}>
          <div style={{ width: "100%", position: "sticky", top: 110 }}>
            <Preview {...textProps} isEdit={isEdit} />
          </div>
        </div>
      </div>
      {!embedded && (
        <div className={styles.footerBar}>
          <div className={styles.footerInner}>
            <div className={styles.info}>
              <Icon name="check-all" size="24" />
              {"Last edit "}
              <span>{dayjs(new Date()).format("MMM DD, YYYY - HH:mm[h]")}</span>
              <u style={{ marginLeft: 8 }}>@Alfonso</u>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                disabled={loading || !text}
                onClick={runManualSave}
                style={{ marginLeft: 20 }}
                className={classNames("button", styles.button)}
              >
                {loading ? "Loading..." : <span>Save changes</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddText;
